import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router';
import { Truck, Lock, Mail, LogIn, Eye, EyeOff, Loader2, ShieldCheck, MessageSquare } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { authService } from '../services/authService';
import { SESSION_EXPIRED_MESSAGE_KEY } from '../services/apiService';
import dogusLogo from '@/assets/599ad72d49c8887573a4750887df95ce60e36150.png';

export function Login() {
  const navigate = useNavigate();
  const { login, completeTwoFactorLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [twoFactorPending, setTwoFactorPending] = useState(false);
  const [twoFactorEmail, setTwoFactorEmail] = useState('');
  const [twoFactorMaskedPhone, setTwoFactorMaskedPhone] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);

  useEffect(() => {
    try {
      const message = sessionStorage.getItem(SESSION_EXPIRED_MESSAGE_KEY);
      if (message) {
        sessionStorage.removeItem(SESSION_EXPIRED_MESSAGE_KEY);
        setError(message);
      }
    } catch {
      // storage erişilemezse sessizce geç
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Lütfen e-posta adresinizi girin');
      return;
    }

    if (!password.trim()) {
      setError('Lütfen şifrenizi girin');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const result = await login(email.trim(), password);

      if (result.requiresTwoFactor) {
        setTwoFactorPending(true);
        setTwoFactorEmail(result.email || email.trim());
        setTwoFactorMaskedPhone(result.maskedPhone || '');
        setTwoFactorCode('');
        setError('');
        toast.info('Telefonunuza SMS doğrulama kodu gönderildi');
      } else if (result.success) {
        toast.success('Giriş başarılı!');
        navigate('/');
      } else {
        setError(result.message || 'Hatalı e-posta veya şifre');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Giriş yapılırken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!twoFactorCode.trim()) {
      setError('Lütfen SMS doğrulama kodunuzu girin');
      return;
    }

    setError('');
    setTwoFactorLoading(true);

    try {
      const result = await completeTwoFactorLogin(twoFactorEmail, twoFactorCode);
      if (result.success) {
        toast.success('Giriş başarılı!');
        navigate('/');
      } else {
        setError(result.message || 'Doğrulama başarısız');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Doğrulama yapılırken bir hata oluştu');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail.trim()) {
      toast.error('Lütfen e-posta adresinizi girin');
      return;
    }
    try {
      setForgotPasswordLoading(true);
      const result = await authService.forgotPassword(forgotPasswordEmail.trim());
      if (!result.success) throw new Error(result.message || 'Şifre gönderilemedi');
      toast.success(result.message || 'Yeni şifre e-posta adresinize gönderildi');
      setForgotPasswordOpen(false);
      setForgotPasswordEmail('');
    } catch (error: any) {
      toast.error(error?.message || 'Şifre gönderilemedi');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div className="auth-page safe-area-page min-h-screen bg-gradient-to-br from-[#120700] via-[#241006] to-[#140904] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img src={dogusLogo} alt="Doğuş Logo" className="w-24 h-auto" />
          </div>
          <h1 className="auth-title mb-2 text-3xl font-bold text-white">Yük Ortağım</h1>
          <p className="auth-subtitle text-sm text-orange-100">Size verilen yetkili giriş bilgileriyle giriş yapınız</p>
        </div>

        <div className="auth-card rounded-2xl border border-orange-500/20 bg-[#111111] p-8 shadow-xl">
          {twoFactorPending ? (
            <form onSubmit={handleTwoFactorSubmit} className="space-y-6">
              {error && (
                <div className="auth-error rounded-lg border border-rose-500/20 bg-rose-950/30 px-4 py-3 text-sm text-rose-200">
                  {error}
                </div>
              )}

              <div className="text-center mb-2">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-950/40 mb-4">
                  <ShieldCheck className="w-8 h-8 text-orange-400" />
                </div>
                <h2 className="text-xl font-bold text-white">İki Adımlı Doğrulama</h2>
                <p className="mt-2 text-sm text-slate-300">
                  {twoFactorMaskedPhone} numarasına gönderilen 6 haneli kodu girin
                </p>
              </div>

              <div>
                <label htmlFor="twofactor-code" className="mb-2 block text-sm font-medium text-slate-300">
                  Doğrulama Kodu
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-orange-300" />
                  <input
                    id="twofactor-code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    autoComplete="one-time-code"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full rounded-lg border border-orange-500/20 bg-[#171717] py-3 pl-10 pr-4 text-center text-xl tracking-[0.5em] font-mono text-slate-100 placeholder:text-slate-600 focus:border-orange-400 focus:ring-2 focus:ring-orange-500/30"
                    placeholder="000000"
                    disabled={twoFactorLoading}
                    autoFocus
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={twoFactorLoading}
                className={`flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-500 py-3 font-medium text-white transition-all duration-300 hover:shadow-lg ${
                  twoFactorLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {twoFactorLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Doğrulanıyor...
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-5 h-5" />
                    Doğrula ve Giriş Yap
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => { setTwoFactorPending(false); setError(''); setTwoFactorCode(''); }}
                className="w-full text-sm text-orange-300 hover:text-orange-200 mt-1"
              >
                ← Geri Dön
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
               <div className="auth-error rounded-lg border border-rose-500/20 bg-rose-950/30 px-4 py-3 text-sm text-rose-200">
                 {error}
               </div>
            )}

            <div>
              <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-300">
                E-posta
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-orange-300" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-orange-500/20 bg-[#171717] py-3 pl-10 pr-4 text-slate-100 focus:border-orange-400 focus:ring-2 focus:ring-orange-500/30"
                  placeholder="ornek@email.com"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="mb-2 block text-sm font-medium text-slate-300">
                Şifre
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-orange-300" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-orange-500/20 bg-[#171717] py-3 pl-10 pr-12 text-slate-100 focus:border-orange-400 focus:ring-2 focus:ring-orange-500/30"
                  placeholder="••••••••"
                  disabled={loading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="mt-2 text-right">
                <button type="button" onClick={() => { setForgotPasswordEmail(email); setForgotPasswordOpen(true); }} className="text-sm font-medium text-orange-300 hover:text-orange-200">
                  Şifremi Unuttum
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-500 py-3 font-medium text-white transition-all duration-300 hover:shadow-lg ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Giriş yapılıyor...
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  Giriş Yap
                </>
              )}
            </button>
          </form>
          )}

          {!twoFactorPending && (
          <div className="mt-6 space-y-3 border-t border-orange-500/10 pt-6">
            <Link
              to="/driver-mobile-auth"
              className="auth-primary-link block rounded-lg bg-gradient-to-r from-orange-500 to-amber-500 py-3 text-center text-sm font-medium text-white shadow-md transition-all hover:shadow-lg"
            >
              📱 Sürücü Mobil Giriş (Telefon ile)
            </Link>

          </div>
          )}
        </div>

        <div className="auth-footer-links mt-6 flex items-center justify-center gap-4 text-sm text-slate-400">
          <Link to="/about" className="transition-colors hover:text-orange-300">Hakkında</Link>
          <span>•</span>
          <Link to="/disclosure" className="transition-colors hover:text-orange-300">Aydınlatma Metni</Link>
          <span>•</span>
          <Link to="/privacy" className="transition-colors hover:text-orange-300">Gizlilik</Link>
          <span>•</span>
          <Link to="/explicit-consent" className="transition-colors hover:text-orange-300">Açık Rıza</Link>
          <span>•</span>
          <Link to="/electronic-consent" className="transition-colors hover:text-orange-300">Ticari İleti</Link>
        </div>

        {forgotPasswordOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setForgotPasswordOpen(false)}>
            <div className="w-full max-w-md rounded-2xl border border-orange-500/20 bg-[#111111] p-6 shadow-xl" onClick={(event) => event.stopPropagation()}>
              <h3 className="text-xl font-bold text-white">Şifremi Unuttum</h3>
              <p className="mt-2 text-sm text-slate-300">E-posta adresinizi girin. Sistemde kayıtlıysa yeni geçici şifreniz bu adrese gönderilir.</p>
              <div className="mt-4">
                <label className="mb-2 block text-sm font-medium text-slate-300">E-posta</label>
                <input type="email" value={forgotPasswordEmail} onChange={(e) => setForgotPasswordEmail(e.target.value)} className="w-full rounded-lg border border-orange-500/20 bg-[#171717] px-4 py-3 text-slate-100 focus:border-orange-400 focus:ring-2 focus:ring-orange-500/30" placeholder="ornek@email.com" />
              </div>
              <div className="mt-5 flex justify-end gap-3">
                <button type="button" onClick={() => setForgotPasswordOpen(false)} className="rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-[#1a1a1a]">Vazgeç</button>
                <button type="button" onClick={() => void handleForgotPassword()} disabled={forgotPasswordLoading} className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
                  {forgotPasswordLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {forgotPasswordLoading ? 'Gönderiliyor...' : 'Yeni Şifre Gönder'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
